import { useState } from "react";
import {
    Dialog,
    DialogContent,
    TextField,
    Button,
    Stack,
    Typography,
    Alert,
    CircularProgress,
    Divider,
    Link,
    IconButton,
    InputAdornment,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../auth/FirebaseContext";
import Logo from "./Logo";

const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

export default function RegistrationDialog({ open, onClose, onSwitchToLogin }) {
    const { t } = useTranslation();
    const { register, loginWithGoogle } = useAuthContext();
    const [formData, setFormData] = useState({
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleChange = (field) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!PASSWORD_REGEX.test(formData.password)) {
            setError(t("auth.passwordPolicy"));
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            setError(t("auth.passwordsNoMatch"));
            return;
        }

        setLoading(true);
        try {
            await register(formData.email, formData.password, formData.phone);
            onClose();
        } catch (err) {
            if (err.code === "auth/email-already-in-use") {
                setError(t("auth.emailInUse"));
            } else {
                setError(t("auth.registerError"));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
            <DialogContent>
                <Stack alignItems="center" sx={{ mb: 3 }}>
                    <Logo size={48} />
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 1.5 }}>
                        {t("auth.registerTitle")}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                        {t("auth.registerSubtitle")}
                    </Typography>
                </Stack>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Button
                    fullWidth
                    variant="outlined"
                    onClick={async () => {
                        setError("");
                        try {
                            await loginWithGoogle();
                            onClose();
                        } catch (err) {
                            if (err.code !== "auth/popup-closed-by-user") {
                                setError(t("auth.googleError", { message: err.message || t("auth.tryAgain") }));
                            }
                        }
                    }}
                    startIcon={<Icon icon="flat-color-icons:google" width={22} />}
                    sx={{
                        mb: 2,
                        py: 1.2,
                        borderRadius: 2,
                        borderColor: "grey.300",
                        color: "text.primary",
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "0.95rem",
                        "&:hover": { borderColor: "grey.400", bgcolor: "grey.50" },
                    }}
                >
                    {t("auth.googleContinue")}
                </Button>

                <Divider sx={{ my: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        {t("auth.orCreateAccount")}
                    </Typography>
                </Divider>

                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            fullWidth
                            label={t("auth.emailRequired")}
                            type="email"
                            value={formData.email}
                            onChange={handleChange("email")}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon icon="mdi:email-outline" width={20} color="#919EAB" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Stack spacing={0.5}>
                            <TextField
                                fullWidth
                                label={t("auth.phoneRequired")}
                                placeholder="+381 6X XXX XXXX"
                                value={formData.phone}
                                onChange={handleChange("phone")}
                                required
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Icon icon="mdi:phone-outline" width={20} color="#919EAB" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 1.5 }}>
                                {t("auth.phoneHint")}
                            </Typography>
                        </Stack>
                        <TextField
                            fullWidth
                            label={t("auth.passwordRequired")}
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={handleChange("password")}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon icon="mdi:lock-outline" width={20} color="#919EAB" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                            aria-label={showPassword ? t("common.hidePassword") : t("common.showPassword")}
                                        >
                                            <Icon icon={showPassword ? "mdi:eye-off" : "mdi:eye"} width={20} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            helperText={t("auth.passwordPolicyHint")}
                        />
                        <TextField
                            fullWidth
                            label={t("auth.confirmPasswordRequired")}
                            type={showPassword ? "text" : "password"}
                            value={formData.confirmPassword}
                            onChange={handleChange("confirmPassword")}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon icon="mdi:lock-check-outline" width={20} color="#919EAB" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            type="submit"
                            size="large"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                            sx={{ py: 1.3, borderRadius: 2, fontWeight: 700 }}
                        >
                            {loading ? t("auth.registering") : t("auth.registerSubmit")}
                        </Button>
                    </Stack>
                </form>

                <Typography sx={{ mt: 2.5, textAlign: "center" }} variant="body2">
                    {t("auth.haveAccount")}{" "}
                    <Link component="button" onClick={onSwitchToLogin} sx={{ fontWeight: 700 }}>
                        {t("auth.loginCta")}
                    </Link>
                </Typography>
            </DialogContent>
        </Dialog>
    );
}
